/**
 * AllBaise Wishes Hub — Platform where people post wishes and others fulfill them
 */
import { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { useAuth } from "@/App";
import { toast } from "sonner";
import TopNav from "@/components/TopNav";
import GeoFilterBar from "@/components/GeoFilterBar";
import {
  Loader2, Star, Search, Plus, X, MapPin,
  DollarSign, Clock, Check, MessageCircle, Users,
} from "lucide-react";

const API = (() => {
  const host = window.location.hostname;
  if (host === "localhost" || host === "127.0.0.1") return "http://localhost:8001/api";
  return `https://api.${host}/api`;
})();

const getHeaders = () => {
  const token = localStorage.getItem("token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const WISH_CATEGORIES = [
  "Services", "Products", "Jobs", "Learning", "Health",
  "Travel", "Technology", "Home", "Fashion", "Food",
  "Entertainment", "Volunteering", "Mentorship", "Funding", "Other"
];

function WishCard({ wish, onOffer, onFulfill, user }) {
  const [offerMsg, setOfferMsg] = useState("");
  const [showOffer, setShowOffer] = useState(false);
  const isOwner = user?.user_id === wish.posted_by;
  const isFulfilled = wish.status === "fulfilled";

  return (
    <Card className={`bg-app-card dark:bg-app-card light:bg-white border-white/10 dark:border-white/10 light:border-gray-200 p-4 ${isFulfilled ? "opacity-70" : ""}`}>
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-pink-500 to-rose-600 flex items-center justify-center text-white shrink-0">
          <Star className="w-5 h-5" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between">
            <div>
              <h3 className="text-sm font-semibold">{wish.title}</h3>
              <p className="text-[10px] text-zinc-500">
                by {wish.poster_name || "Anonymous"} {wish.location && `• ${wish.location}`}
              </p>
            </div>
            <Badge className={`text-[9px] px-2 py-0.5 ${isFulfilled ? "bg-emerald-600/30 text-emerald-400" : "bg-amber-600/30 text-amber-400"}`}>
              {isFulfilled ? "Fulfilled" : "Open"}
            </Badge>
          </div>

          <p className="text-xs text-zinc-500 mt-2 line-clamp-3">{wish.description}</p>

          <div className="flex flex-wrap items-center gap-2 mt-2">
            <Badge className="bg-violet-600/20 text-violet-400 text-[9px] px-2 py-0.5">{wish.category}</Badge>
            {wish.budget && (
              <span className="text-[10px] text-emerald-500 flex items-center gap-1">
                <DollarSign className="w-3 h-3" /> {wish.budget}
              </span>
            )}
            {wish.location && (
              <span className="text-[10px] text-zinc-500 flex items-center gap-1">
                <MapPin className="w-3 h-3" /> {wish.location}
              </span>
            )}
            <span className="text-[10px] text-zinc-500 flex items-center gap-1">
              <Clock className="w-3 h-3" />
              {new Date(wish.created_at).toLocaleDateString()}
            </span>
          </div>

          {wish.tags && wish.tags.length > 0 && (
            <div className="flex flex-wrap gap-1 mt-2">
              {wish.tags.map((tag, i) => (
                <span key={i} className="text-[9px] bg-pink-900/40 text-pink-200 px-1.5 py-0.5 rounded">{tag}</span>
              ))}
            </div>
          )}

          {/* Offers count */}
          {wish.offers?.length > 0 && (
            <p className="text-[10px] text-zinc-500 mt-2 flex items-center gap-1">
              <Users className="w-3 h-3" /> {wish.offers.length} offer(s)
            </p>
          )}

          {/* Fulfilled by */}
          {isFulfilled && wish.fulfilled_by_name && (
            <p className="text-[10px] text-emerald-500 mt-2 flex items-center gap-1">
              <Check className="w-3 h-3" /> Fulfilled by {wish.fulfilled_by_name}
            </p>
          )}

          {/* Actions */}
          <div className="flex items-center gap-2 mt-3 pt-2 border-t border-white/10 dark:border-white/10 light:border-gray-200">
            {!isOwner && !isFulfilled && (
              <Button onClick={() => setShowOffer(!showOffer)} size="sm"
                className="h-7 text-[10px] bg-pink-600 hover:bg-pink-500 text-white">
                <MessageCircle className="w-3 h-3 mr-1" />
                {showOffer ? "Cancel" : "I Can Help"}
              </Button>
            )}
            {isOwner && !isFulfilled && wish.offers?.length > 0 && (
              <div className="text-[10px] text-zinc-500">
                {wish.offers.map((offer, i) => (
                  <div key={i} className="flex items-center gap-2 mt-1">
                    <span>{offer.name}: "{offer.message}"</span>
                    <Button onClick={() => onFulfill(wish.id, offer.offer_id)} size="sm"
                      className="h-6 text-[8px] bg-emerald-600 hover:bg-emerald-500 text-white">
                      <Check className="w-2.5 h-2.5 mr-1" /> Accept
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Offer form */}
          {showOffer && (
            <div className="mt-2 space-y-2">
              <textarea value={offerMsg} onChange={e => setOfferMsg(e.target.value)}
                placeholder="Describe how you can help..."
                className="w-full bg-zinc-800/50 dark:bg-zinc-800/50 light:bg-gray-100 border border-zinc-700 dark:border-zinc-700 light:border-gray-300 rounded-lg p-2 text-xs text-app focus:outline-none focus:border-pink-500" rows={2} />
              <Button onClick={() => onOffer(wish.id, offerMsg)} disabled={!offerMsg.trim()}
                className="h-7 text-[10px] bg-pink-600 hover:bg-pink-500 text-white">
                <SendIcon className="w-3 h-3 mr-1" /> Send Offer
              </Button>
            </div>
          )}
        </div>
      </div>
    </Card>
  );
}

function SendIcon(props) {
  return (
    <svg {...props} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="22" y1="2" x2="11" y2="13" /><polygon points="22 2 15 22 11 13 2 9 22 2" />
    </svg>
  );
}

export default function WishCentre() {
  const auth = useAuth();
  const user = auth.user;
  const [wishes, setWishes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("all");
  const [country, setCountry] = useState("all");
  const [region, setRegion] = useState("all");
  const [radiusKm, setRadiusKm] = useState("");
  const [filter, setFilter] = useState("open");
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ title: "", description: "", category: "Services", location: "", budget: "", tags: "" });
  const [creating, setCreating] = useState(false);

  const fetchWishes = () => {
    setLoading(true);
    axios.get(`${API}/wishes/list`, { headers: getHeaders() })
      .then(res => setWishes(res.data.items || []))
      .catch(() => setWishes([]))
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchWishes(); }, []);

  const filtered = wishes.filter(w => {
    if (filter === "open" && w.status !== "open") return false;
    if (filter === "fulfilled" && w.status !== "fulfilled") return false;
    if (category !== "all" && w.category !== category) return false;
    if (country !== "all" && w.country !== country) return false;
    if (region !== "all" && w.province !== region) return false;
    if (radiusKm && w.distance_km && parseFloat(w.distance_km) > parseFloat(radiusKm)) return false;
    if (region !== "all" && w.province !== region) return false;
    if (search && !w.title.toLowerCase().includes(search.toLowerCase()) && !w.description.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  const handleCreate = async () => {
    if (!form.title || !form.description) {
      toast.error("Title and description are required"); return;
    }
    setCreating(true);
    try {
      await axios.post(`${API}/wishes/create`, {
        ...form,
        tags: form.tags ? form.tags.split(",").map(t => t.trim()).filter(Boolean) : [],
      }, { headers: getHeaders() });
      toast.success("Wish posted!");
      setShowCreate(false);
      setForm({ title: "", description: "", category: "Services", location: "", budget: "", tags: "" });
      fetchWishes();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to post wish"); }
    setCreating(false);
  };

  const handleOffer = async (wishId, message) => {
    if (!message.trim()) return;
    try {
      await axios.post(`${API}/wishes/${wishId}/offer?message=${encodeURIComponent(message)}`, {}, { headers: getHeaders() });
      toast.success("Offer submitted!");
      fetchWishes();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to submit offer"); }
  };

  const handleFulfill = async (wishId, offerId) => {
    try {
      await axios.post(`${API}/wishes/${wishId}/fulfill?offer_id=${offerId}`, {}, { headers: getHeaders() });
      toast.success("Wish fulfilled!");
      fetchWishes();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to fulfill"); }
  };

  return (
    <div className="min-h-screen bg-app text-app">
      <TopNav />
      <div className="max-w-6xl mx-auto px-4 py-4">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-xl font-bold flex items-center gap-2">
              <Star className="w-5 h-5 text-pink-400" /> Wishes Hub
            </h1>
            <p className="text-xs text-zinc-500">Post wishes, help others, make dreams come true</p>
          </div>
          <Button onClick={() => setShowCreate(!showCreate)} className="bg-pink-600 hover:bg-pink-500 text-white text-xs h-8">
            {showCreate ? <X className="w-3.5 h-3.5 mr-1" /> : <Plus className="w-3.5 h-3.5 mr-1" />}
            {showCreate ? "Cancel" : "Make a Wish"}
          </Button>
        </div>

        {showCreate && (
          <Card className="bg-app-card dark:bg-app-card light:bg-white border-white/10 dark:border-white/10 light:border-gray-200 p-4 mb-4">
            <h2 className="text-sm font-semibold mb-3">New Wish</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              <div className="space-y-1">
                <label className="text-[10px] text-zinc-500">Title *</label>
                <input type="text" value={form.title} onChange={e => setForm({...form, title: e.target.value})}
                  className="w-full bg-pink-950/40 border border-pink-800/40 rounded-lg px-3 py-1.5 text-xs text-app focus:outline-none focus:border-pink-500" />
              </div>
              <div className="space-y-1">
                <label className="text-[10px] text-zinc-500">Category</label>
                <select value={form.category} onChange={e => setForm({...form, category: e.target.value})}
                  className="w-full bg-pink-950/40 border border-pink-800/40 rounded-lg px-3 py-1.5 text-xs text-app focus:outline-none focus:border-pink-500">
                  {WISH_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div className="space-y-1">
                <label className="text-[10px] text-zinc-500">Location</label>
                <input type="text" value={form.location} onChange={e => setForm({...form, location: e.target.value})}
                  className="w-full bg-pink-950/40 border border-pink-800/40 rounded-lg px-3 py-1.5 text-xs text-app focus:outline-none focus:border-pink-500" />
              </div>
              <div className="space-y-1">
                <label className="text-[10px] text-zinc-500">Budget (optional)</label>
                <input type="text" value={form.budget} onChange={e => setForm({...form, budget: e.target.value})}
                  className="w-full bg-pink-950/40 border border-pink-800/40 rounded-lg px-3 py-1.5 text-xs text-app focus:outline-none focus:border-pink-500" />
              </div>
              <div className="space-y-1">
                <label className="text-[10px] text-zinc-500">Tags (comma separated)</label>
                <input type="text" value={form.tags} onChange={e => setForm({...form, tags: e.target.value})}
                  className="w-full bg-pink-950/40 border border-pink-800/40 rounded-lg px-3 py-1.5 text-xs text-app focus:outline-none focus:border-pink-500" />
              </div>
            </div>
            <div className="space-y-1 mt-3">
              <label className="text-[10px] text-zinc-500">Description *</label>
              <textarea value={form.description} onChange={e => setForm({...form, description: e.target.value})} rows={2}
                className="w-full bg-pink-950/40 border border-pink-800/40 rounded-lg px-3 py-1.5 text-xs text-app focus:outline-none focus:border-pink-500" />
            </div>
            <Button onClick={handleCreate} disabled={creating}
              className="mt-3 bg-pink-600 hover:bg-pink-500 text-white text-xs h-8">
              {creating ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Star className="w-3 h-3 mr-1" />}
              Post Wish
            </Button>
          </Card>
        )}

        {/* Filters */}
        <div className="flex flex-wrap items-center gap-2 mb-4">
          <div className="relative flex-1 min-w-[200px] max-w-xs">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-zinc-400" />
            <input type="text" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search wishes..."
              className="w-full bg-zinc-800/50 dark:bg-zinc-800/50 light:bg-gray-100 border border-zinc-700 dark:border-zinc-700 light:border-gray-300 rounded-lg pl-8 pr-3 py-1.5 text-xs text-app placeholder:text-zinc-500 focus:outline-none focus:border-pink-500" />
          </div>
          <div className="flex gap-1">
            <button onClick={() => setFilter("open")}
              className={`text-[10px] px-2.5 py-1 rounded-full ${filter === "open" ? "bg-pink-600/30 text-pink-300" : "bg-zinc-800/50 text-zinc-500 hover:text-zinc-300"}`}>Open</button>
            <button onClick={() => setFilter("fulfilled")}
              className={`text-[10px] px-2.5 py-1 rounded-full ${filter === "fulfilled" ? "bg-emerald-600/30 text-emerald-300" : "bg-zinc-800/50 text-zinc-500 hover:text-zinc-300"}`}>Fulfilled</button>
            <button onClick={() => setFilter("all")}
              className={`text-[10px] px-2.5 py-1 rounded-full ${filter === "all" ? "bg-violet-600/30 text-violet-300" : "bg-zinc-800/50 text-zinc-500 hover:text-zinc-300"}`}>All</button>
          </div>
        </div>

        {/* Category pills */}
        <div className="flex gap-1.5 mb-4 overflow-x-auto pb-1">
          <button onClick={() => setCategory("all")}
            className={`text-[10px] px-3 py-1 rounded-full whitespace-nowrap ${category === "all" ? "bg-pink-600/30 text-pink-300" : "bg-zinc-800/50 text-zinc-500 hover:text-zinc-300"}`}>All</button>
          {WISH_CATEGORIES.map(c => (
            <button key={c} onClick={() => setCategory(c)}
              className={`text-[10px] px-3 py-1 rounded-full whitespace-nowrap ${category === c ? "bg-pink-600/30 text-pink-300" : "bg-zinc-800/50 text-zinc-500 hover:text-zinc-300"}`}>{c}</button>
          ))}
        </div>

        {/* Wish List */}
        {loading ? (
          <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-zinc-500" /></div>
        ) : filtered.length === 0 ? (
          <Card className="bg-app-card dark:bg-app-card light:bg-white border-white/10 dark:border-white/10 light:border-gray-200 p-8 text-center">
            <Star className="w-8 h-8 text-zinc-600 mx-auto mb-2" />
            <p className="text-sm text-zinc-500">No wishes found</p>
            <Button onClick={() => setShowCreate(true)} variant="outline" size="sm" className="mt-3 text-xs border-pink-600 text-pink-500">
              <Plus className="w-3 h-3 mr-1" /> Make a wish
            </Button>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {filtered.map(wish => (
              <WishCard key={wish.id} wish={wish} onOffer={handleOffer} onFulfill={handleFulfill} user={user} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
